import type { HTMLAttributes, Ref, TdHTMLAttributes, ThHTMLAttributes } from 'react'

import cn from '@components/utils/cn'

const Root = ({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLTableElement> & { ref?: Ref<HTMLTableElement> }) => (
  <div className="tw:relative tw:w-full tw:overflow-auto">
    <table ref={ref} className={cn('tw:w-full tw:caption-bottom tw:text-sm', className)} {...props} />
  </div>
)

const Header = ({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLTableSectionElement> & { ref?: Ref<HTMLTableSectionElement> }) => (
  <thead ref={ref} className={cn('tw:[&_tr]:border-b', className)} {...props} />
)

const Body = ({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLTableSectionElement> & { ref?: Ref<HTMLTableSectionElement> }) => (
  <tbody ref={ref} className={cn('tw:[&_tr:last-child]:border-0', className)} {...props} />
)

const Footer = ({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLTableSectionElement> & { ref?: Ref<HTMLTableSectionElement> }) => (
  <tfoot
    ref={ref}
    className={cn('tw:border-t tw:bg-gray-cool-50 tw:font-medium tw:last:[&>tr]:border-b-0', className)}
    {...props}
  />
)

const Row = ({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLTableRowElement> & { ref?: Ref<HTMLTableRowElement> }) => (
  <tr
    ref={ref}
    className={cn(
      'tw:border-b tw:transition-colors tw:hover:bg-gray-cool-50 tw:data-[state=selected]:bg-gray-cool-100',
      'tw:data-[current=true]:bg-gray-100',
      className
    )}
    {...props}
  />
)

const Head = ({
  className,
  ref,
  ...props
}: ThHTMLAttributes<HTMLTableCellElement> & { ref?: Ref<HTMLTableCellElement> }) => (
  <th
    ref={ref}
    className={cn(
      'tw:h-12 tw:px-4 tw:text-left tw:align-middle tw:font-medium tw:text-gray-cool-500 tw:has-[[role=checkbox]]:pr-0',
      className
    )}
    {...props}
  />
)

const Cell = ({
  className,
  ref,
  ...props
}: TdHTMLAttributes<HTMLTableCellElement> & { ref?: Ref<HTMLTableCellElement> }) => (
  <td
    ref={ref}
    className={cn('tw:px-4 tw:py-4 tw:align-middle tw:has-[[role=checkbox]]:pr-0', className)}
    {...props}
  />
)

const Caption = ({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLTableCaptionElement> & { ref?: Ref<HTMLTableCaptionElement> }) => (
  <caption ref={ref} className={cn('tw:mt-4 tw:text-sm tw:text-gray-cool-500', className)} {...props} />
)

const Table = {
  Root,
  Header,
  Body,
  Footer,
  Head,
  Row,
  Cell,
  Caption,
}

export { Table }
